/**
 * Social links in the site settings document (`site_settings.social`).
 *
 * Wire shape (backend `AdminSettingsDoc.Social`, an untyped JSONB array; seeded from
 * backend/seed/data/site_settings.json): `[{ "platform": "linkedin", "url": "https://…" }]`.
 * The public site renders one footer link per entry keyed by `platform` and emits every
 * `url` as an Organization `sameAs`, so a platform must be unique and a url must be an
 * absolute https URL. The backend enforces the same rules on PUT /admin/settings (422 per
 * field); validating here too gives editors the error before they save.
 */

export interface SocialLink {
  platform: string;
  url: string;
}

/** Platforms the public footer has a display label for (frontend footer.tsx `platformLabel`). */
export const SOCIAL_PLATFORMS = [
  'linkedin',
  'x',
  'facebook',
  'instagram',
  'youtube',
  'github',
  'tiktok',
] as const;

/** Read `social` defensively: anything that is not a `{platform,url}` string pair is dropped. */
export function asSocialLinks(v: unknown): SocialLink[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((x) => {
    if (!x || typeof x !== 'object') return [];
    const o = x as Record<string, unknown>;
    if (typeof o.platform !== 'string' || typeof o.url !== 'string') return [];
    return [{ platform: o.platform, url: o.url }];
  });
}

/** The payload form: trimmed, platform lower-cased (the footer and the seed use lower-case keys). */
export function normalizeSocialLinks(links: SocialLink[]): SocialLink[] {
  return links.map((l) => ({ platform: l.platform.trim().toLowerCase(), url: l.url.trim() }));
}

export function socialLinksEqual(a: SocialLink[], b: SocialLink[]): boolean {
  return JSON.stringify(normalizeSocialLinks(a)) === JSON.stringify(normalizeSocialLinks(b));
}

export type SocialUrlError = 'required' | 'invalid' | 'https';
export type SocialPlatformError = 'required' | 'duplicate';

export interface SocialRowErrors {
  platform?: SocialPlatformError;
  url?: SocialUrlError;
}

/** An absolute `https:` URL with a host and no embedded credentials. */
export function validateSocialUrl(raw: string): SocialUrlError | undefined {
  const value = raw.trim();
  if (!value) return 'required';
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return 'invalid';
  }
  if (parsed.protocol !== 'https:') return 'https';
  if (!parsed.hostname || !parsed.hostname.includes('.') || parsed.username || parsed.password) {
    return 'invalid';
  }
  return undefined;
}

/** Per-row errors (index-aligned with `links`); an all-empty object means the row is valid. */
export function validateSocialLinks(links: SocialLink[]): SocialRowErrors[] {
  const normalized = normalizeSocialLinks(links);
  const counts = new Map<string, number>();
  for (const l of normalized) {
    if (l.platform) counts.set(l.platform, (counts.get(l.platform) ?? 0) + 1);
  }
  return normalized.map((l) => {
    const errors: SocialRowErrors = {};
    if (!l.platform) errors.platform = 'required';
    else if ((counts.get(l.platform) ?? 0) > 1) errors.platform = 'duplicate';
    const url = validateSocialUrl(l.url);
    if (url) errors.url = url;
    return errors;
  });
}

export function hasSocialErrors(errors: SocialRowErrors[]): boolean {
  return errors.some((e) => e.platform !== undefined || e.url !== undefined);
}
